import { z } from "zod";
import { getErrorFromCatch, serializeFieldErrors } from "../api/api";
import { roleApi } from "../api/role.api";
import { INewRole, IRoleGoup } from "../model/IRole";
import { msg } from "../msg";
import checkFormStateByZod from "../utils/checkFormStateByZod";
import trimItems from "../utils/thisItems";
import { IHookApiResponse } from "./IHookApiResponse";
import { useAccess } from "./useAccess";

export function useRole(group: IRoleGoup) {
  // -- Check permissions
  const { getSettingAccess } = useAccess();

  // --
  // -- Data checking scheme
  // --
  const newRoleRequestSchema = z.object({
    name: z
      .string()
      // .transform(str => str.trim())
      // .pipe(z.string()
      .min(3, msg('role-id-too-short', { len: 3 }))
      .min(1, msg('role-no-id'))
    // ),
  } as Record<keyof INewRole, any>);

  // --
  // -- API Methods
  // --
  const [tryToAddRole] = roleApi.useAddRoleMutation();
  const [tryToDeleteRole] = roleApi.useDeleteRoleMutation();
  const [tryToEditRole] = roleApi.useEditRoleMutation();

  // --
  // -- Get current group roles
  // --
  const { data: agentRoles, isSuccess: agentRolesGettingSuccess, isError: agentRolesGettingError } = roleApi.useGetAgentRolesQuery({}, { skip: group !== 'agent' });
  const { data: vendorRoles, isSuccess: vendorRolesGettingSuccess, isError: vendorRolesGettingError } = roleApi.useGetVendorRolesQuery({}, { skip: group !== 'vendor' });

  // --
  // -- Add Role
  // --
  const addRole = async (data: INewRole): IHookApiResponse<INewRole> => {
    // 1. Check permissions
    if (!getSettingAccess('add_role'))
      return { totalError: msg('not-permissions') };

    // 2. Prepare data
    const preparedData = trimItems(data);

    // 3. Check data
    const errs = checkFormStateByZod(newRoleRequestSchema, preparedData);
    if (errs && Object.values(errs)?.length) return errs;

    // 4. Send data
    try {
      await tryToAddRole(preparedData).unwrap();
      return null;
    } catch (err) {
      return {
        ...serializeFieldErrors(err),
        totalError: `${msg('role-add-failed')} \n${getErrorFromCatch(err)}`
      }
    }
  };


  // --
  // -- Edit Role
  // --
  const editRole = async (data: INewRole): IHookApiResponse<INewRole> => {
    // 1. Check permissions
    if (!getSettingAccess('add_role'))
      return { totalError: msg('not-permissions') };

    // 2. Prepare data
    const preparedData = trimItems(data);

    // 3. Check data
    const errs = checkFormStateByZod(newRoleRequestSchema, preparedData);
    if (errs && Object.values(errs)?.length) return errs;

    // 4. Send data
    try {
      await tryToEditRole(preparedData).unwrap();
      return null;
    } catch (err) {
      return {
        ...serializeFieldErrors(err),
        totalError: `${msg('role-edit-failed')} \n${getErrorFromCatch(err)}`
      }
    }
  };


  // --
  // -- Add Role
  // --
  const deleteRole = async (id: number): IHookApiResponse<{}> => {
    // 1. Check permissions
    if (!getSettingAccess('delete-role'))
      return { totalError: msg('not-permissions') };

    // 2. Check data
    if (!id)
      return { totalError: msg('role-no-id') };

    // 4. Send data
    try {
      await tryToDeleteRole(id).unwrap();
      return null;
    } catch (err) {
      return {
        ...serializeFieldErrors(err),
        totalError: `${msg('role-add-failed')} \n${getErrorFromCatch(err)}`
      }
    }
  };

  return {
    addRole,
    deleteRole,
    editRole,
    roles: {
      agent: agentRoles,
      vendor: vendorRoles,
    }[group],
    rolesPending: {
      agent: !agentRolesGettingSuccess && !agentRolesGettingError,
      vendor: !vendorRolesGettingSuccess && !vendorRolesGettingError,
    }[group]
  };
}